import { formatPostcode } from './selection';

// Match the existing external-delivery booking rules; never change the fee engine.
export function recommendedDriverFee(areaFeePence: number): number | null {
  if (!Number.isInteger(areaFeePence) || areaFeePence < 0 || areaFeePence > 5000) return null;
  return Math.max(300, Math.ceil(areaFeePence / 50) * 50);
}

export type DriverAreaQuote = {
  deliveryFeePence: number;
  fuelSupplementPence: number;
  hubFeePence: number;
  shopDebtPence: number;
  zoneLabel?: string | null;
};

export async function roundTripDrivingMiles(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
  postcode: string,
  mapboxToken: string,
  signal: AbortSignal,
  destination?: {lat:number;lng:number},
): Promise<number | null> {
  const pc = formatPostcode(postcode);
  if ((!pc && !destination) || !Number.isFinite(latitude) || !Number.isFinite(longitude) || !mapboxToken) return null;
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) controller.abort();
  const timer = setTimeout(abort, 7000);
  try {
    let lat=destination?.lat,lng=destination?.lng;
    if(!destination){
      const geo = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc!)}`, { signal: controller.signal });
      if (!geo.ok) return null;
      const json=await geo.json();lat=json.result?.latitude;lng=json.result?.longitude;
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    // Three waypoints include the real return leg (one-way roads can differ).
    const points = `${longitude},${latitude};${lng},${lat};${longitude},${latitude}`;
    const route = await fetch(`https://api.mapbox.com/directions/v5/mapbox/driving/${points}?access_token=${encodeURIComponent(mapboxToken)}&overview=false&steps=false&continue_straight=false`, { signal: controller.signal });
    if (!route.ok) return null;
    const data = await route.json();
    const metres = data.code === 'Ok' ? data.routes?.[0]?.distance : null;
    return typeof metres === 'number' && Number.isFinite(metres) && metres >= 0 ? Math.round(metres / 1609.344 * 10) / 10 : null;
  } catch { return null; }
  finally { clearTimeout(timer); signal.removeEventListener('abort', abort); }
}

// Advisory only: leave a 50p reduction alone; flag reductions of at least 25%.
export function lowDriverFeeMessage(feePence:number,recommendedPence:number|null|undefined,miles:number|null|undefined):string|null {
 if(!Number.isSafeInteger(feePence)||feePence<=0||!Number.isSafeInteger(recommendedPence)||recommendedPence! - feePence<=50||feePence*4>recommendedPence!*3)return null;
 const trip=typeof miles==='number'&&Number.isFinite(miles)&&miles>0?`this approximately ${miles.toFixed(1)}-mile round trip`:'this round trip';
 return `This fee is low for ${trip}. Drivers may be put off accepting this job.`;
}
